/* eslint-disable i18next/no-literal-string */
import {Button} from '@dropbox/dig-components/buttons';
import {SegmentedControl} from '@dropbox/dig-components/controls';
import {Spinner} from '@dropbox/dig-components/progress_indicators';
import {SystemBanner} from '@dropbox/dig-components/system_banner';
import {Tabs} from '@dropbox/dig-components/tabs';
import {Text} from '@dropbox/dig-components/typography';
import {Box, Stack} from '@dropbox/dig-foundations';
import {useQuery} from '@tanstack/react-query';
import {AdminWireframeService, ApiError} from 'client';
import {Layout} from 'components/DSYS/Layout';
import {getService} from 'utilities';
import {
  formatWorkstreamTimeframeLabel,
  WorkstreamTimeframe,
} from 'views/workstreams/detail/workstreamTimeframe';
import {EmptyState} from 'views/workstreams/page/WorkstreamsControls';

import {WireframeCompanyView} from './WireframeCompanyView';
import {WireframePillarView} from './WireframePillarView';
import {toNumber} from './wireframeReportFormat';
import {
  isSameHalfReportKey,
  WireframeReportView,
  wireframeReportViewQueryKey,
} from './wireframeReportQuery';
import {UnpricedNote} from './WireframeUnpricedNote';

const COMPANY_TAB = 'company';
const pillarTab = (id: number) => `pillar:${id}`;

const errorStatus = (error: unknown) => (error instanceof ApiError ? error.status : undefined);

/**
 * /workstreams Wireframes: Finance's read-only headcount and spend
 * report for the half. Company, or one pillar, follows the rail's pillar (the
 * tabs pick it the same way), so a pillar's tab is linkable. Full or Adjusted
 * (the inputs less their adjustments) applies to every tab, and the page keeps
 * it in the URL too. Mount it only for Finance admins: it fetches as soon as it
 * renders.
 */
export const WireframesPane = ({
  timeframe,
  pillarName,
  onSelectPillar,
  view,
  onSelectView,
  hrefForWorkstream,
}: {
  timeframe: WorkstreamTimeframe;
  // The rail's pillar; null (or one the report doesn't have) is Company.
  pillarName: string | null;
  onSelectPillar: (pillarName: string | null) => void;
  view: WireframeReportView;
  onSelectView: (view: WireframeReportView) => void;
  hrefForWorkstream: (id: number) => string;
}) => {
  const query = useQuery({
    queryKey: wireframeReportViewQueryKey(timeframe, view),
    queryFn: () =>
      getService(AdminWireframeService).getWireframeReportApiV1AdminWireframeReportGet(
        timeframe,
        view
      ),
    // Switching views keeps the tabs (and the toggle) up while the other view
    // loads; its numbers aren't shown until they're the requested view's.
    placeholderData: (previous, previousQuery) =>
      previousQuery && isSameHalfReportKey(previousQuery.queryKey, timeframe)
        ? previous
        : undefined,
    // Other Finance admins edit the inputs, so reopening the view refetches
    // (showing the cached report meanwhile) instead of the app's 30-minute default.
    staleTime: 0,
    // A denied or unplanned half won't change on retry.
    retry: (failureCount, error) => (errorStatus(error) ?? 500) >= 500 && failureCount < 2,
  });
  const report = query.data;
  const timeframeLabel = formatWorkstreamTimeframeLabel(timeframe);
  // Labeled from the response, so adjusted numbers never pass for full ones.
  const reportLabel = report?.view === 'adjusted' ? `${timeframeLabel} · Adjusted` : timeframeLabel;

  if (query.isPending) {
    return (
      <Box display="flex" justifyContent="center" padding="24">
        <Spinner aria-label="Loading wireframe report" />
      </Box>
    );
  }
  // The half has no planning root yet.
  if (errorStatus(query.error) === 404) {
    return (
      <EmptyState
        title="No plan for this half"
        detail={`${timeframeLabel} has no planning narrative to report on. Try another timeframe.`}
      />
    );
  }
  if (query.isError || !report) {
    return (
      <Layout.MaxWidth width="wide" inset>
        <Box paddingY="24">
          <SystemBanner
            tone="alert"
            withActions={
              <Box display="flex" columnGap="8">
                {/* The toggle only shows with a report, so offer the way back here. */}
                {view === 'adjusted' && (
                  <Button variant="transparent" onClick={() => onSelectView('full')}>
                    Show full report
                  </Button>
                )}
                <Button variant="transparent" onClick={() => void query.refetch()}>
                  Retry
                </Button>
              </Box>
            }
          >
            <Text>
              {errorStatus(query.error) === 403
                ? 'The wireframe report needs Finance access.'
                : 'Unable to load the wireframe report.'}
            </Text>
          </SystemBanner>
        </Box>
      </Layout.MaxWidth>
    );
  }
  if (report.pillars.length === 0) {
    return (
      <EmptyState
        title="No pillars in this half"
        detail="Wireframes summarize headcount by pillar. Try another timeframe."
      />
    );
  }

  const pillar = report.pillars.find((candidate) => candidate.name === pillarName);
  const selectedTab = pillar ? pillarTab(pillar.pillar_workstream_id) : COMPANY_TAB;
  const unpriced = toNumber(report.spend.unpriced_headcount_fte);

  return (
    <Layout.MaxWidth width="wide" inset>
      <Box paddingTop="16" paddingBottom="32">
        <Tabs
          selectedTab={selectedTab}
          onSelection={(tabId: string) =>
            onSelectPillar(
              report.pillars.find(
                (candidate) => pillarTab(candidate.pillar_workstream_id) === tabId
              )?.name ?? null
            )
          }
        >
          {/* The divider spans the toggle too, so the row reads as one header. */}
          <Box
            display="flex"
            alignItems="center"
            columnGap="16"
            borderBottom="Solid"
            borderColor="Border Subtle"
          >
            <Box flexGrow={1} minWidth="0">
              <Tabs.Group hasHorizontalPadding={false} hasDivider={false}>
                <Tabs.Tab id={COMPANY_TAB}>Company</Tabs.Tab>
                {report.pillars.map((candidate) => (
                  <Tabs.Tab
                    key={candidate.pillar_workstream_id}
                    id={pillarTab(candidate.pillar_workstream_id)}
                  >
                    {candidate.name}
                  </Tabs.Tab>
                ))}
              </Tabs.Group>
            </Box>
            <SegmentedControl
              aria-label="Report numbers"
              selectedValue={view}
              onSelection={(value) => {
                // The report always has a view, so re-picking the selected one is a no-op.
                if (!value) return;
                const next: WireframeReportView = value === 'adjusted' ? 'adjusted' : 'full';
                if (next !== view) onSelectView(next);
              }}
            >
              <SegmentedControl.Option variant="text" value="full">
                Full
              </SegmentedControl.Option>
              <SegmentedControl.Option variant="text" value="adjusted">
                Adjusted
              </SegmentedControl.Option>
            </SegmentedControl>
          </Box>
          <Tabs.Panel tabId={selectedTab}>
            <Box paddingTop="24">
              {query.isPlaceholderData ? (
                <Box display="flex" justifyContent="center" padding="24">
                  <Spinner aria-label="Loading wireframe report" />
                </Box>
              ) : pillar ? (
                <WireframePillarView
                  pillar={pillar}
                  timeframeLabel={reportLabel}
                  hrefForWorkstream={hrefForWorkstream}
                />
              ) : (
                <Stack gap="16">
                  {unpriced > 0 && <UnpricedNote fte={unpriced} />}
                  <WireframeCompanyView
                    report={report}
                    timeframeLabel={reportLabel}
                    hrefForWorkstream={hrefForWorkstream}
                  />
                </Stack>
              )}
            </Box>
          </Tabs.Panel>
        </Tabs>
      </Box>
    </Layout.MaxWidth>
  );
};
